import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import {
  UserDocument,
  UserDriverLic,
  User,
  UserUpdateDamageWaiverStatusRequest,
  UserDocumentStatus,
  UpdateInsuranceDocStatusRequest,
  UserDocumentType,
} from '../../../clients/quipli-service/user/user';
import { Card } from '../../../components/card/card';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Button } from '../../../components/form/button';
import { Toggle } from '../../../components/form/toggle';
import Icon from '../../../components/icons/icon';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { Typography } from '../../../components/typography/typography';
import { UserDocumentForm } from './user-document-form';
import { InsuranceDocument } from '../../../clients/customer-api/customer/customer-customer';
import { displayMoneyFormat } from '../../../util/util';
import { StateProvince } from '../../../clients/quipli-service/locales/locales';
import { toast } from '../../../util/toasts';

export const DocumentItem = ({
  object,
  driverLicense,
  otherCheckoutLabel,
  userReqDamageWaiver,
  setUserReqDamageWaiver,
  reload,
  user,
  stateProvinceList,
  insurance,
}: {
  object: UserDocument;
  driverLicense?: UserDriverLic;
  otherCheckoutLabel?: string;
  userReqDamageWaiver?: boolean;
  setUserReqDamageWaiver: (v: boolean) => void;
  reload: () => void;
  user: User;
  stateProvinceList: StateProvince[];
  insurance?: InsuranceDocument;
}) => {
  const {
    deleteUserDocument,
    deleteUserDriverLicense,
    updateUserDamageWaiver,
    updateDocumentInsuranceStatus,
    deleteInsuranceDocument,
    downloadUserDocumentFile,
  } = useAPI();
  const [isModalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false),
    [isModalEditOpen, setModalEditOpen] = useState<boolean>(false),
    [insuranceDamageWaiverRequired, setInsuranceDamageWaiverRequired] = useState<boolean>(userReqDamageWaiver ?? false);

  enum documentTypeLabel {
    Insurance = 'Insurance',
    License = 'Drivers License',
    OtherCheckout = 'Other (Checkout)',
    Other = 'Other',
  }

  const updateDamageWaiver = async () => {
    if (!object.id && !insurance?.id) return;
    await updateUserDamageWaiver({
      userId: user.id,
      hasDamageWaiverRequired: insuranceDamageWaiverRequired,
    } as UserUpdateDamageWaiverStatusRequest);
    setUserReqDamageWaiver(insuranceDamageWaiverRequired);
    reload();
    toast('Damage Waiver updated');
  };

  const updateDocumentStatus = async (approve: boolean) => {
    if (!object?.id) return;

    await updateDocumentInsuranceStatus({
      userId: user.id,
      documentId: object.id,
      documentStatus: approve ? UserDocumentStatus.Approved : UserDocumentStatus.Rejected,
    } as UpdateInsuranceDocStatusRequest);

    reload();
    toast(`Document ${approve ? 'approved' : 'declined'}`);
  };

  const removeItem = async () => {
    if (object.id && object.documentType !== UserDocumentType.Insurance) {
      await deleteUserDocument(object.id);
    }

    if (object.documentType === UserDocumentType.License && driverLicense) {
      await deleteUserDriverLicense(driverLicense.userId);
    } else if (object.documentType === UserDocumentType.Insurance) {
      if (insurance?.id) {
        await deleteInsuranceDocument(user.id, insurance.id);
      }
      if (insurance?.file?.id) {
        await updateDocumentInsuranceStatus({
          userId: user.id,
          documentId: insurance.file.id,
          documentStatus: UserDocumentStatus.Deleted,
        } as UpdateInsuranceDocStatusRequest);
      }
    }

    setModalDeleteOpen(false);
    reload();
    toast('Document deleted');
  };

  useEffect(() => {
    if (insuranceDamageWaiverRequired !== userReqDamageWaiver) updateDamageWaiver();
  }, [insuranceDamageWaiverRequired]);

  const downloadUserFile = async (userId: number, fileId: number) => {
    const url = await downloadUserDocumentFile(userId, fileId);

    window.open(url, '_blank');
  };

  return (
    <>
      <Card className="p-4">
        <div className="title">
          <Typography type="text-md" weight="semi-bold" className="text-overflow">
            {object.documentType == UserDocumentType.OtherCheckout && otherCheckoutLabel
              ? otherCheckoutLabel
              : documentTypeLabel[object.documentType as UserDocumentType]}
          </Typography>
          <div>
            <Dropdown
              trigger={() => (
                <Button className="button-tertiary" data-cy={`customer_menu`}>
                  <Icon icon="DotsHorizontal" />
                </Button>
              )}
            >
              <div>
                <div
                  className="flex gap-2"
                  onClick={() => {
                    setModalEditOpen(true);
                  }}
                  data-cy="disable_customer"
                >
                  <Typography type="text-sm" className="text-quipli-black-800">
                    Edit document
                  </Typography>
                </div>
              </div>
              <div>
                <div
                  className="flex gap-2"
                  onClick={() => {
                    setModalDeleteOpen(true);
                  }}
                  data-cy="delete_customer"
                >
                  <Typography type="text-sm" className="text-error-500">
                    Delete document
                  </Typography>
                </div>
              </div>
            </Dropdown>
          </div>
        </div>
        <div className="h-[1px] w-full bg-grey-300"></div>

        <div className="fields-section text-sm w-full">
          {object?.fileName && (
            <>
              <div className="field w-full">
                <Typography type="text-sm" weight="medium" className="property text-grey-500 w-1/5">
                  Document name
                </Typography>

                <div
                  className="w-4/5 flex gap-x-[10px] items-center justify-between cursor-pointer"
                  onClick={() => downloadUserFile(object.userId, object.id)}
                >
                  <div className="shrink break-words w-[calc(100%_-_52px_-_10px)]">
                    <Typography type="text-sm" className="text-sky-600">
                      {object.fileName}
                    </Typography>
                  </div>

                  <Icon icon="Download"></Icon>
                </div>
              </div>

              <div className="field">
                <Typography type="text-sm" weight="medium" className="property text-grey-500">
                  Uploaded at
                </Typography>

                <Typography type="text-sm" className="date-value text-grey-800">
                  <span>{`${DateTime.fromISO(object.createdAt, { setZone: true }).toFormat('DD')}`}</span>
                  <span>{`${DateTime.fromISO(object.createdAt, { setZone: true }).toFormat('t')}`}</span>
                </Typography>
              </div>
            </>
          )}

          {object.documentType === UserDocumentType.Insurance ? (
            <>
              {insurance && <InsuranceItem insurance={insurance} />}

              <div>
                <div className="field">
                  <Typography type="text-sm" weight="medium" className="property text-grey-400">
                    Damage Waiver
                  </Typography>

                  <Toggle
                    onClick={() => {
                      setInsuranceDamageWaiverRequired(!insuranceDamageWaiverRequired);
                    }}
                    checked={insuranceDamageWaiverRequired}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-400">
                      Status
                    </Typography>
                    <span>{object.status}</span>
                    {object.status === UserDocumentStatus.Approved && (
                      <Icon icon="CheckCircle" size="medium" stroke="var(--success-500)" />
                    )}
                    {object.status === UserDocumentStatus.Rejected && (
                      <Icon icon="XCircle" size="medium" stroke="var(--error-500)" />
                    )}
                  </div>
                  {object.status === UserDocumentStatus.Pending ? (
                    <div className="flex justify-end">
                      <div className="flex flex-col sm:flex-row justify-end">
                        <Button
                          className="button button-secondary h-[40px]"
                          onClick={() => {
                            updateDocumentStatus(false);
                          }}
                        >
                          Decline
                        </Button>

                        <Button
                          className="save-button ml-3 button-yellow h-[40px]"
                          onClick={() => {
                            updateDocumentStatus(true);
                          }}
                        >
                          Approve
                        </Button>
                      </div>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </>
          ) : object.documentType === UserDocumentType.License && driverLicense ? (
            <DriverLicenseItem driverLicense={driverLicense}></DriverLicenseItem>
          ) : (
            ''
          )}
        </div>

        <ConfirmModal
          isOpen={isModalDeleteOpen}
          close={() => setModalDeleteOpen(false)}
          onConfirm={removeItem}
          headerIcon={
            <div className="flex justify-center">
              <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
            </div>
          }
          prompt="Remove item?"
          additionalText="This cannot be undone"
          noLabel="Cancel"
          yesLabel="Remove"
        />

        <UserDocumentForm
          isOpen={isModalEditOpen}
          setIsOpen={setModalEditOpen}
          isEdit={true}
          userDocument={object}
          userDriverLicense={object.documentType === UserDocumentType.License ? driverLicense : undefined}
          customLabel={otherCheckoutLabel}
          user={user}
          stateProvinceList={stateProvinceList}
          existingFile={object}
          insuranceDocument={object.documentType === UserDocumentType.Insurance ? insurance : undefined}
          onSubmit={reload}
        ></UserDocumentForm>
      </Card>
    </>
  );
};

export const DriverLicenseItem = ({ driverLicense }: { driverLicense: UserDriverLic }) => {
  return (
    <>
      <div className="fields-section text-sm w-full">
        <div>
          <div className="field">
            <Typography type="text-sm" weight="medium" className="property text-grey-400">
              Full Name
            </Typography>

            <span>{driverLicense.fullName}</span>
          </div>

          <div className="field">
            <Typography type="text-sm" weight="medium" className="property text-grey-400">
              Date of Birth
            </Typography>

            <span>{driverLicense.dateOfBirth ? driverLicense.dateOfBirth.toFormat('LL/dd/yyyy') : '-'}</span>
          </div>

          <div className="field">
            <Typography type="text-sm" weight="medium" className="property text-grey-400">
              License Number
            </Typography>

            <span>{driverLicense.driverLicNum}</span>
          </div>

          <div className="field">
            <Typography type="text-sm" weight="medium" className="property text-grey-400">
              Issuing State/Province
            </Typography>

            <span>{driverLicense.state ? driverLicense.state.name : '-'}</span>
          </div>

          <div className="field">
            <Typography type="text-sm" weight="medium" className="property text-grey-400">
              Expiration Date
            </Typography>

            <span>{driverLicense.expirationDate.toFormat('LL/dd/yyyy')}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export const InsuranceItem = ({ insurance }: { insurance: InsuranceDocument }) => {
  return insurance.id ? (
    <>
      <div className="fields-section text-sm w-full gap-4">
        <div className="field flex gap-4 mb-1">
          <>
            <Typography type="text-sm" weight="medium" className="property text-grey-400">
              Full Name
            </Typography>

            <span>
              {insurance.firstName} {insurance.lastName}
            </span>
          </>
        </div>
        <div className="field mb-1">
          <Typography type="text-sm" weight="medium" className="property text-grey-400">
            Issued By
          </Typography>

          <span>{insurance.issuedBy}</span>
        </div>
        <div className="field mb-1">
          <Typography type="text-sm" weight="medium" className="property text-grey-400">
            Policy Number
          </Typography>

          <span>{insurance.policyNumber}</span>
        </div>
        <div className="field mb-1">
          <Typography type="text-sm" weight="medium" className="property text-grey-400">
            Expiry Date
          </Typography>

          <span>{insurance.expiryDate!.toFormat('LLL d, yyyy')}</span>
        </div>

        <div className="field mb-1">
          <Typography type="text-sm" weight="medium" className="property text-grey-400">
            Issued Date
          </Typography>

          <span>{insurance.issuedDate!.toFormat('LLL d, yyyy')}</span>
        </div>
        <div className="field mb-1">
          <Typography type="text-sm" weight="medium" className="property text-grey-400">
            Coverage Amount
          </Typography>

          <span>{insurance.coverageAmount ? displayMoneyFormat(insurance.coverageAmount) : '-'}</span>
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};
